import {
  calculateTimetableSchedules,
  resolveScheduleColours,
  ExistingScheduleEntity,
  TimetableScheduleBlock,
  TimetableScheduleBlockInput,
  TimetableScheduleNormalizationError,
  TimetableScheduleTrack,
  // Deep import avoids @weareopenr/common's top-level barrel, which also re-exports its zod
  // codecs - those ship .d.cts files using TS 5 syntax this project's TS 4.9 can't parse.
} from '@weareopenr/common/build/src/validationHelper/timetable-schedule';
import { DayOfWeek } from '@weareopenr/types';
import { ITimetableSchedule } from 'src/api/getTimetable';
import { getEntityId, getEntityType, isMenuSchedule, TimetableEntityType } from './helpers';

const DAY_END_TIME = '23:59';
const DAY_START_TIME = '00:00';

const TRACK_BY_ENTITY_TYPE: Record<TimetableEntityType, TimetableScheduleTrack> = {
  menu: 'menu',
  priceScheme: 'price_scheme',
};

// Ids are client-only placeholders until a real create-schedule API exists to replace them.
// crypto.randomUUID() rather than Math.random() so this doesn't trip security-sensitive-PRNG
// linting, even though nothing here is actually security-sensitive (never sent to the server).
const generateOptimisticId = () => `optimistic-${crypto.randomUUID()}`;

// A block before its end_time has been derived (see deriveEndTimes) - TimetableScheduleBlockInput
// itself now requires end_time, so this is the shape used while a day's blocks are still being
// assembled and sorted.
type BlockBoundary = Omit<TimetableScheduleBlockInput, 'end_time'>;

const addOneMinute = (time: string): string => {
  const hours = Number(time.slice(0, 2));
  const minutes = Number(time.slice(3, 5));
  const total = (hours * 60 + minutes + 1) % (24 * 60);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

const subtractOneMinute = (time: string): string => {
  const hours = Number(time.slice(0, 2));
  const minutes = Number(time.slice(3, 5));
  const total = (hours * 60 + minutes - 1 + 24 * 60) % (24 * 60);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

// calculateTimetableSchedules now requires every input block to already carry its own end_time
// (it validates contiguity rather than inferring it), so this derives it the way the library used
// to internally: sorted by start_time, each boundary's end is one minute before the next one's
// start, and the last always runs to the end of the day.
const deriveEndTimes = (boundaries: BlockBoundary[]): TimetableScheduleBlockInput[] => {
  const sorted = [...boundaries].sort((a, b) => a.start_time.localeCompare(b.start_time));
  return sorted.map((boundary, index) => {
    const nextBoundary = sorted[index + 1];
    return { ...boundary, end_time: nextBoundary ? subtractOneMinute(nextBoundary.start_time) : DAY_END_TIME };
  });
};

const makeBlock = (
  day: DayOfWeek,
  startTime: string,
  entityId: string,
  track: TimetableScheduleTrack,
): BlockBoundary =>
  track === 'menu'
    ? { day_of_week: day, start_time: startTime, menu_id: entityId }
    : { day_of_week: day, start_time: startTime, price_scheme_id: entityId };

const toBlockInput = (schedule: ITimetableSchedule): BlockBoundary =>
  isMenuSchedule(schedule)
    ? { day_of_week: schedule.day_of_week, start_time: schedule.start_time, menu_id: schedule.menu_id }
    : { day_of_week: schedule.day_of_week, start_time: schedule.start_time, price_scheme_id: schedule.price_scheme_id };

const getBlockEntityId = (block: TimetableScheduleBlock, track: TimetableScheduleTrack): string =>
  (track === 'menu' ? block.menu_id : block.price_scheme_id) as string;

const getInputBlockEntityId = (block: BlockBoundary, track: TimetableScheduleTrack): string =>
  (track === 'menu' ? block.menu_id : block.price_scheme_id) as string;

const toSchedule = (
  block: TimetableScheduleBlock,
  track: TimetableScheduleTrack,
  colourOrder: number | null,
  name: string,
): ITimetableSchedule => {
  const base = {
    schedule_id: generateOptimisticId(),
    day_of_week: block.day_of_week,
    start_time: block.start_time,
    end_time: block.end_time,
    name,
    colour_order: colourOrder,
  };

  return track === 'menu'
    ? { ...base, menu_id: getBlockEntityId(block, track), price_scheme_id: null }
    : { ...base, menu_id: null, price_scheme_id: getBlockEntityId(block, track) };
};

export interface AddScheduleInput {
  entityType: TimetableEntityType;
  entityId: string;
  entityName: string;
  days: DayOfWeek[];
  startTime: string;
  endTime: string;
  // The lane's remainder - before the chosen start, and after the chosen end - falls back to
  // whichever menu/price scheme the timetable is configured to default to.
  defaultEntityId: string;
  defaultEntityName: string;
  // Both of the timetable's defaults, regardless of `entityType` - resolveScheduleColours needs
  // both to recognise a default-entity block on either track, since a menu/price scheme default
  // always resolves to the fixed "liquorice" colour (colour_order: null) rather than a palette one.
  defaultMenuId: string;
  defaultPriceSchemeId: string;
}

export type AddScheduleResult =
  | { success: true; schedules: ITimetableSchedule[]; blocks: TimetableScheduleBlock[] }
  | { success: false; error: TimetableScheduleNormalizationError };

/**
 * Whichever pre-existing block was covering `time` before the edit - i.e. the one with the
 * latest start_time at or before it - since a day's existing blocks are always already
 * gap-free (see the docstring below), so every point in the day has exactly one owner unless
 * the day had no schedule at all for this track. Falls back to the default entity only in that
 * fully-empty case.
 */
const getEntityCoveringTime = (
  time: string,
  existingDayBlocks: BlockBoundary[],
  track: TimetableScheduleTrack,
  defaultEntityId: string,
): string => {
  const covering = [...existingDayBlocks]
    .filter((block) => block.start_time <= time)
    .sort((a, b) => b.start_time.localeCompare(a.start_time))[0];

  return covering ? getInputBlockEntityId(covering, track) : defaultEntityId;
};

/**
 * Each block's end_time is derived afterwards by deriveEndTimes, purely from the *next* block's
 * start time - a block built here never carries its own end time yet. So to make a chosen end
 * time actually hold, we back it with a real boundary one minute later, unless something already
 * starts there - resuming whatever block originally covered that time if the new entry was
 * inserted into the middle of it, or the default entity if the day had no coverage there at all.
 * The same applies in reverse for the start time, since the earliest block in a day is always
 * forced to start at 00:00 - though there resuming is automatic, since any earlier block is left
 * in place and simply gets its end time truncated by the new one's start.
 *
 * Any existing block whose own start_time falls inside [startTime, endTime] - not just one that
 * happens to start at exactly `input.startTime` - is wholly superseded and dropped, however many
 * of them that is: the new block's chosen bounds must hold even when it overlaps two or more
 * existing blocks at once, rather than leaving a stray fragment of one sitting inside its range
 * and eating into its end time. A block that starts before `input.startTime` is left in place and
 * simply truncated (see above); one that starts after `input.endTime` is untouched.
 */
const buildBlocksForDay = (
  day: DayOfWeek,
  existingDayBlocks: BlockBoundary[],
  input: AddScheduleInput,
  track: TimetableScheduleTrack,
): BlockBoundary[] => {
  const supersededBlocks = existingDayBlocks.filter(
    (block) => block.start_time < input.startTime || block.start_time > input.endTime,
  );
  const blocks = [...supersededBlocks, makeBlock(day, input.startTime, input.entityId, track)];

  const hasEarlierBlock = existingDayBlocks.some((block) => block.start_time < input.startTime);
  if (!hasEarlierBlock && input.startTime !== DAY_START_TIME) {
    blocks.push(makeBlock(day, DAY_START_TIME, input.defaultEntityId, track));
  }

  const afterEndTime = addOneMinute(input.endTime);
  const hasAdjacentBlock = blocks.some((block) => block.start_time === afterEndTime);
  if (input.endTime !== DAY_END_TIME && !hasAdjacentBlock) {
    const resumedEntityId = getEntityCoveringTime(afterEndTime, existingDayBlocks, track, input.defaultEntityId);
    blocks.push(makeBlock(day, afterEndTime, resumedEntityId, track));
  }

  return blocks;
};

/**
 * A (day, track) lane is the atomic unit `calculateTimetableSchedules` recomputes (see its
 * docstring), so adding one block to a day replaces every block in that day's menu (or price
 * scheme) lane with a freshly recalculated, gap-free set - not just the block being added.
 */
export const addScheduleOptimistically = (
  existingSchedules: ITimetableSchedule[],
  input: AddScheduleInput,
): AddScheduleResult => {
  const track = TRACK_BY_ENTITY_TYPE[input.entityType];
  const affectedDays = new Set(input.days);
  const isAffected = (schedule: ITimetableSchedule) =>
    affectedDays.has(schedule.day_of_week) && getEntityType(schedule) === input.entityType;

  const affectedSchedules = existingSchedules.filter(isAffected);
  const untouchedSchedules = existingSchedules.filter((schedule) => !isAffected(schedule));

  const allBlocks: TimetableScheduleBlockInput[] = input.days.flatMap((day) => {
    const existingDayBlocks = affectedSchedules.filter((schedule) => schedule.day_of_week === day).map(toBlockInput);
    return deriveEndTimes(buildBlocksForDay(day, existingDayBlocks, input, track));
  });

  const result = calculateTimetableSchedules(allBlocks);
  if (result.success === false) return result;

  const nameByEntityId = new Map(affectedSchedules.map((schedule) => [getEntityId(schedule), schedule.name]));
  nameByEntityId.set(input.entityId, input.entityName);
  nameByEntityId.set(input.defaultEntityId, input.defaultEntityName);

  // The normalized blocks for just the affected (day, track) groups - exactly the payload
  // PATCH /timetables/{timetableId} expects, since it fully replaces whichever (day, track)
  // groups are present and leaves the rest of the timetable untouched.
  const blocks = result.schedules.flatMap((group) => group.blocks);

  // Mirrors the server's own colour resolution (see resolveScheduleColours's docstring) against
  // every schedule currently in the timetable - not just the affected days - so a menu/price
  // scheme that already has a colour elsewhere in the timetable keeps it in the live preview too,
  // instead of picking up whatever colour its position within the day happens to land on.
  const existingSchedule: ExistingScheduleEntity[] = existingSchedules.map((schedule) => ({
    menu_id: schedule.menu_id,
    price_scheme_id: schedule.price_scheme_id,
    colour_order: schedule.colour_order,
  }));
  const colouredBlocks = resolveScheduleColours(blocks, {
    defaultMenuId: input.defaultMenuId,
    defaultPriceSchemeId: input.defaultPriceSchemeId,
    existingSchedule,
  });

  const recomputedSchedules = colouredBlocks.map((block) =>
    toSchedule(
      block,
      track,
      block.colour_order,
      nameByEntityId.get(getBlockEntityId(block, track)) ?? input.entityName,
    ),
  );

  return { success: true, schedules: [...untouchedSchedules, ...recomputedSchedules], blocks };
};
